import { useQuery, UseQueryOptions } from "@tanstack/react-query";
import axios from "@utils/axios";
import { Stages } from "@/types/session";

export interface SessionStatusParams {
  evseID: string;
  sessionID: string;
}

const fetchSessionStatus = async (params: SessionStatusParams, signal?: AbortSignal): Promise<Stages> => {
  const response = await axios.get(`/api/evse/${params.evseID}/payment-success/${params.sessionID}/`, { signal });
  return response.data as Stages;
};

type QueryKeyType = readonly ["session", "status", SessionStatusParams | "disabled"];

export const sessionStatusKeys = {
  all: ["session"] as const,
  statuses: () => [...sessionStatusKeys.all, "status"] as const,
  status: (params: SessionStatusParams | "disabled") => [...sessionStatusKeys.statuses(), params] as const
};

export const useSessionStatus = (
  params: SessionStatusParams | null,
  options?: Omit<UseQueryOptions<Stages, Error, Stages, QueryKeyType>, "queryKey" | "queryFn">
) => {
  const isEnabled = params !== null && !!params.evseID && !!params.sessionID;

  return useQuery({
    queryKey: params ? sessionStatusKeys.status(params) : sessionStatusKeys.status("disabled"),
    queryFn: ({ signal }) => {
      if (!params) {
        throw new Error("Session status params are required");
      }
      return fetchSessionStatus(params, signal);
    },
    staleTime: 10 * 1000, // 10 seconds: session status changes frequently
    cacheTime: 2 * 60 * 1000, // 2 minutes
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    refetchInterval: 5000, // Refetch every 5 seconds for active sessions
    enabled: isEnabled,
    ...options
  });
};
